"use client";

import { motion } from "framer-motion";
import React, { useState } from "react";
import { twMerge } from "tailwind-merge";

import FlickeringGrid from "@/components/magicui/flickeringGrid";
import MaxWidthWrapper from "@/components/shared/max-width-wrapper";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Link } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import * as m from "@/paraglide/messages";
import { useAuth } from "@/services/context/auth/AuthContext";

const container = {
  hidden: { opacity: 0, y: -20 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      staggerChildren: 0.2,

      when: "beforeChildren",
    },
  },
};

const item = {
  hidden: { opacity: 0, y: -20 },
  show: { opacity: 1, y: 0 },
};

const content = (m: any) => ({
  sectionLabel: m.dry_alert_cobra_harbor(),
  title: m.home_new_gull_hope(),
  description: m.legal_dirty_opossum_startle(),
  buttons: [
    {
      text: m.get_started(),
      href: "#pricing",
      variant: "default", // Assuming 'default' is a predefined variant in shadcn
    },
  ],
});

const TradingFeature = () => {
  const { user } = useAuth();
  const activeClass =
    "data-[state=on]:bg-transparent data-[state=on]:border-b-2 data-[state=on]:text-primary data-[state=on]:border-primary";
  const [tradeAction, setTradeAction] = useState<string>("buy");
  const [tradeType, setTradeType] = useState<string>("market");
  return (
    <section id="trading">
      <div
        id="portfolio"
        className="relative scroll-pt-10 pt-8  lg:pt-24 pb-16 lg:pb-28 bg-gradiant-dark dark:bg-gradiant-light"
      >
        <div className="absolute inset-x-0 -top-11 mt-[calc(-3/16*1rem)] flex items-end">
          <div className="mr-[calc(-1*(theme(spacing.8)-theme(spacing[1.5])))] h-11 flex-auto dark:bg-zinc-50 bg-zinc-900"></div>
          <div className="mx-auto flex w-full justify-between px-6 sm:max-w-[40rem] md:max-w-[48rem] md:px-8 lg:max-w-[64rem] 0 xl:max-w-[80rem] dark:text-zinc-50 text-zinc-900">
            <svg
              viewBox="0 0 56 48"
              aria-hidden="true"
              className="-ml-1.5 mb-[calc(-1/16*1rem)] w-14 flex-none overflow-visible fill-current"
            >
              <path d="M 2.686 3 H -4 V 48 H 56 V 47 H 53.314 A 8 8 0 0 1 47.657 44.657 L 8.343 5.343 A 8 8 0 0 0 2.686 3 Z"></path>
            </svg>
            <svg
              viewBox="0 0 56 48"
              aria-hidden="true"
              className="-mr-1.5 mb-[calc(-1/16*1rem)] w-14 flex-none overflow-visible fill-current"
            >
              <path d="M 53.314 3 H 60 V 48 H 0 V 47 H 2.686 A 8 8 0 0 0 8.343 44.657 L 47.657 5.343 A 8 8 0 0 1 53.314 3 Z"></path>
            </svg>
          </div>
          <div className="ml-[calc(-1*(theme(spacing.8)-theme(spacing[1.5])))] h-11 flex-auto dark:bg-zinc-50 bg-zinc-900"></div>
        </div>
        <FlickeringGrid
          className="[mask-image:radial-gradient(ellipse_at_top,white,transparent,transparent)] z-0 absolute  inset-0 size-full"
          squareSize={2}
          gridGap={8}
          color="#6B7280"
          maxOpacity={0.6}
          flickerChance={0.3}
          height={500}
        />
        <MaxWidthWrapper>
          <section className="flex flex-col items-center justify-between gap-2 lg:flex-row">
            <motion.div
              className="flex flex-col items-center lg:items-start gap-4  lg:w-7/12"
              variants={container}
              initial="hidden"
              animate="show"
              viewport={{ once: true, amount: 0.5 }}
            >
              <motion.div variants={item}>
                <Badge variant="secondary">{content(m).sectionLabel}</Badge>
              </motion.div>
              <motion.div variants={item}>
                <h2 className="text-transparent  text-2xl lg:text-4xl font-extrabold tracking-tight leading-normal  pb-4 text-balance bg-gradient-to-br from-white dark:from-background from-50% to-zinc-50/10 bg-clip-text shadow dark:shadow-none ">
                  {content(m).title}
                </h2>
              </motion.div>
              <motion.div variants={item}>
                <p className="lg:mb-6 text-balance text-muted dark:text-background shadow dark:shadow-none">
                  {content(m).description}
                </p>
              </motion.div>
              <motion.div className="flex space-x-4" variants={item}>
                {content(m).buttons.map((button, index) => (
                  <Button key={index} variant="secondary" className="shadow">
                    <Link href={button.href}>{button.text}</Link>
                  </Button>
                ))}
              </motion.div>
            </motion.div>
            <motion.div
              className="relative lg:mt-8 flex justify-stretch py-12 lg:w-6/12"
              variants={container}
              initial="hidden"
              animate="show"
              viewport={{ amount: 0.5 }}
            >
              <Card className="w-full">
                <CardContent className="py-4">
                  <Tabs defaultValue="entry" className="w-full">
                    <TabsList className="grid w-full grid-cols-4 text-xs">
                      <TabsTrigger className="text-xs" value="entry">
                        Entry
                      </TabsTrigger>
                      <TabsTrigger className="text-xs" value="stopLoss">
                        Stop Loss
                      </TabsTrigger>
                      <TabsTrigger className="text-xs" value="takeProfit">
                        Take Profit
                      </TabsTrigger>
                      <TabsTrigger className="text-xs" value="trailing">
                        Trailing
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="entry">
                      <ToggleGroup
                        type="single"
                        className="w-full"
                        onValueChange={setTradeAction}
                        value={tradeAction}
                      >
                        <ToggleGroupItem
                          value="buy"
                          disabled={tradeAction === "buy"}
                          className={twMerge(
                            tradeAction === "buy"
                              ? "data-[state=on]:bg-success data-[state=on]:text-white"
                              : "bg-secondary",
                            "w-full disabled:opacity-100",
                          )}
                          aria-label="Toggle buy"
                        >
                          {m.buy()}
                        </ToggleGroupItem>
                        <ToggleGroupItem
                          value="sell"
                          disabled={tradeAction === "sell"}
                          className={twMerge(
                            tradeAction === "sell"
                              ? "data-[state=on]:bg-destructive data-[state=on]:text-white"
                              : "bg-secondary",
                            "w-full disabled:opacity-100",
                          )}
                          aria-label="Toggle sell"
                        >
                          {m.sell()}
                        </ToggleGroupItem>
                      </ToggleGroup>
                      <ToggleGroup
                        type="single"
                        className="w-full"
                        onValueChange={setTradeType}
                        value={tradeType}
                      >
                        <ToggleGroupItem
                          value="market"
                          disabled={tradeType === "market"}
                          className={twMerge(
                            tradeType === "market"
                              ? activeClass
                              : "border-b-2 border-transparent bg-transparent",
                            "w-full rounded-sm hover:bg-transparent disabled:opacity-100",
                          )}
                          aria-label="Toggle market"
                        >
                          {m.market()}
                        </ToggleGroupItem>
                        <ToggleGroupItem
                          value="conditional"
                          disabled={tradeType === "conditional"}
                          className={twMerge(
                            tradeType === "conditional"
                              ? activeClass
                              : "border-b-2 border-transparent bg-transparent",
                            "w-full rounded-sm hover:bg-transparent disabled:opacity-100",
                          )}
                          aria-label="Toggle conditional"
                        >
                          {m.conditional()}
                        </ToggleGroupItem>
                      </ToggleGroup>
                      <div className="mt-4 flex flex-col gap-4">
                        <div
                          className={cn(
                            tradeType === "market" && "bg-secondary",
                            "flex items-center rounded-sm border-2",
                          )}
                        >
                          <span className="ps-3 text-sm">Price</span>
                          <Input
                            className="border-0 text-right shadow-none focus-visible:ring-0"
                            type="number"
                            disabled={tradeType === "market"}
                            value="69420"
                            readOnly
                          />
                          <span className="w-[80px] border-s-2 text-center align-middle text-xs font-semibold">
                            USDT
                          </span>
                        </div>

                        <div className="flex items-center rounded-sm border-2">
                          <span className="ps-3 text-sm">Units</span>
                          <Input
                            className="border-0 text-right shadow-none focus-visible:ring-0"
                            type="number"
                            readOnly
                          />
                          <span className="w-[80px] border-s-2 text-center align-middle text-xs font-semibold">
                            BTC
                          </span>
                        </div>
                      </div>
                    </TabsContent>
                    <TabsContent value="stopLoss">
                      <div className="mt-4 flex flex-col gap-4">
                        <div
                          className={cn(
                            tradeType === "market" && "bg-secondary",
                            "flex items-center rounded-sm border-2",
                          )}
                        >
                          <span className="ps-3 text-sm">Price</span>
                          <Input
                            className="ocus-visible:border-none border-0 text-right shadow-none ring-0 focus-visible:ring-0"
                            type="number"
                            value="69420"
                            readOnly
                          />
                          <span className="w-[80px] border-s-2 text-center align-middle text-xs font-semibold">
                            USDT
                          </span>
                        </div>
                      </div>
                    </TabsContent>
                    <TabsContent value="takeProfit">
                      <div className="mt-4 flex flex-col gap-4">
                        <div
                          className={cn(
                            tradeType === "market" && "bg-secondary",
                            "flex items-center rounded-sm border-2",
                          )}
                        >
                          <span className="ps-3 text-sm">Price</span>
                          <Input
                            className="border-0 text-right shadow-none focus-visible:ring-0"
                            type="number"
                            value="69420"
                            readOnly
                          />
                          <span className="w-[80px] border-s-2 text-center align-middle text-xs font-semibold">
                            USDT
                          </span>
                        </div>

                        <div className="flex items-center rounded-sm border-2">
                          <span className="ps-3 text-sm">Units</span>
                          <Input
                            className="border-0 text-right shadow-none focus-visible:ring-0"
                            type="number"
                            readOnly
                          />
                          <span className="w-[80px] border-s-2 text-center align-middle text-xs font-semibold">
                            BTC
                          </span>
                        </div>
                      </div>
                    </TabsContent>
                    <TabsContent value="trailing">
                      <div className="mt-4 flex flex-col gap-4">
                        <div
                          className={cn(
                            tradeType === "market" && "bg-secondary",
                            "flex items-center rounded-sm border-2",
                          )}
                        >
                          <span className="ps-3 text-sm">Price</span>
                          <Input
                            className="border-0 text-right shadow-none focus-visible:ring-0"
                            type="number"
                            value="69420"
                            readOnly
                          />
                          <span className="w-[80px] border-s-2 text-center align-middle text-xs font-semibold">
                            USDT
                          </span>
                        </div>

                        <div className="flex items-center rounded-sm border-2">
                          <span className="ps-3 text-sm">Units</span>
                          <Input
                            className="border-0 text-right shadow-none focus-visible:ring-0"
                            type="number"
                            readOnly
                          />
                          <span className="w-[80px] border-s-2 text-center align-middle text-xs font-semibold">
                            BTC
                          </span>
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>
                </CardContent>

                <CardFooter>
                  <Link
                    className={cn(
                      buttonVariants({ variant: "default", size: "sm" }),
                      " gap-2 px-4 md:flex w-full",
                    )}
                    href={
                      user?.email
                        ? `/checkout?yearly=true`
                        : `/signup?redirect=/checkout?trial=true`
                    }
                  >
                    <span>{m.place_order()}</span>
                  </Link>
                </CardFooter>
              </Card>
            </motion.div>
          </section>
        </MaxWidthWrapper>
      </div>

      <div className="mx-auto w-full px-6 sm:max-w-[40rem] md:max-w-[48rem] md:px-8 lg:max-w-[64rem] xl:max-w-[80rem]">
        <div className="relative -mx-2.5 flex -bottom-1 -mt-12">
          <svg
            viewBox="0 0 64 48"
            className="w-16 flex-none fill-white dark:fill-background"
            aria-hidden="true"
          >
            <path d="M51.657 2.343 12.343 41.657A8 8 0 0 1 6.686 44H0v4h64V0h-6.686a8 8 0 0 0-5.657 2.343Z"></path>
          </svg>
          <div className="-mx-px flex-auto bg-white dark:bg-background"></div>
          <svg
            viewBox="0 0 64 48"
            className="w-16 flex-none fill-white dark:fill-background"
            aria-hidden="true"
          >
            <path d="m12.343 2.343 39.314 39.314A8 8 0 0 0 57.314 44H64v4H0V0h6.686a8 8 0 0 1 5.657 2.343Z"></path>
          </svg>
        </div>
      </div>
    </section>
  );
};

export default TradingFeature;
